import { useCallback, useEffect, useMemo, useRef, useState, useId, type ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate } from '@tanstack/react-router';
import { useUploader } from '@hooks/useUploader';
import { UploadLimitAlert, UploadLimitAlertAction } from '@components/UploadLimitAlert';
import { UpgradePromptModal } from '@components/UpgradePromptModal';
import { FileChip } from '@components/FileChip';
import { UploadProgress } from '@components/UploadProgress';
import { UploadActions } from '@components/UploadActions';
import { formatBytesToMB, formatBytes } from '@utils/formatBytes';
import { EmptyState } from '@components/EmptyState';
import { EmptyStateIcons } from '@components/EmptyStateIcons';
import { TermsOfServiceAgreement } from '@components/TermsOfServiceAgreement';
import { UploadPrivacyConsentChoice } from '@components/UploadPrivacyConsentChoice';
import './UploaderZone.css';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { isLoggedIn } from '@services/auth';
import { LicenseService } from '@services/LicenseService';
import type { BrandingProfileDto } from '@services/license';
import { UserTier } from '../config/limits';
import {
  type CloudflareRegionHint,
  CLOUDFLARE_REGIONS_ORDER,
  CLOUDFLARE_REGION_DISPLAY_NAMES,
  COUNTRY_TO_AZURE_REGION,
  getCloudflareStorageRegions,
  getAzureStorageRegions,
  getCloudflareRegionForAzure,
  isCloudflareRegion,
  type StorageRegion,
  type StorageProvider,
} from '../config/regions';
import { getRegions, getCurrentRegion } from '@services/api';
import SecretInputBox from '@components/SecretInputBox';
import { toast } from '@services/toast';
import { sliderIndexToRetentionPeriod, buildRetentionPeriodCode, computeExpiresAtIso } from '@utils/retentionPeriod';
import {
  RETENTION_PERIOD_DAYS_MAX,
  RETENTION_PERIOD_MONTHS_MIN,
  RETENTION_PERIOD_MONTHS_MAX,
  RETENTION_PERIOD_YEARS_MIN,
  RETENTION_PERIOD_YEARS_MAX,
  RETENTION_PERIOD_MONTHS_START_INDEX,
  RETENTION_PERIOD_YEARS_START_INDEX,
  RETENTION_PERIOD_SLIDER_MAX,
  RETENTION_PERIOD_COMBOBOX_MAX_INDEX,
} from '../config/upload';
import { isRegionSubdomain } from '@utils/subdomain';

interface UploaderZoneProps {
  onHasFilesChange?: (hasFiles: boolean) => void;
}

function UploaderZone({ onHasFilesChange }: UploaderZoneProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    progress, 
    isUploading, 
    error, 
    uploadLimitError, 
    createUpload, 
    resolveDescriptor, 
    cancel, 
    lastShareLink,
    clearErrors 
  } = useUploader();
  
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const isAuthenticated = isLoggedIn();
  const defaultRetentionPeriodIndex = isAuthenticated ? 15 : 4;
  const [anonTosAccepted, setAnonTosAccepted] = useState<boolean>(() => isAuthenticated);
  const acceptDelayRef = useRef<number | null>(null);
  const [retentionPeriodSliderValue, setRetentionPeriodSliderValue] = useState<number>(defaultRetentionPeriodIndex);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [maxDownloadsInput, setMaxDownloadsInput] = useState('1');
  const [limitDownloads, setLimitDownloads] = useState(false);
  const [notifyByEmail, setNotifyByEmail] = useState(false);

  const isMobileOrTablet = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    if (typeof window.matchMedia === 'function') {
      const coarse = window.matchMedia('(hover: none) and (pointer: coarse)');
      if (coarse.matches) {
        return true;
      }
    }

    const ua = (navigator.userAgent || '').toLowerCase();
    return ua.includes('android') || ua.includes('iphone') || ua.includes('ipad');
  }, []);

  const dropzoneHintText = isMobileOrTablet ? t('upload.tapToSelect') : t('upload.dropOrClick');
  const [azureRegions, setAzureRegions] = useState<string[]>([]);
  const [cloudflareRegions, setCloudflareRegions] = useState<CloudflareRegionHint[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<StorageRegion | undefined>();
  const [currentAzureRegion, setCurrentAzureRegion] = useState<string | undefined>();
  const [isRegionEnabled, setIsRegionEnabled] = useState(false);
  const [regionsLoaded, setRegionsLoaded] = useState(false);
  const [shareLinkAcknowledged, setShareLinkAcknowledged] = useState(false);
  const [hasLicense, setHasLicense] = useState<boolean | null>(null);
  const [brandingProfiles, setBrandingProfiles] = useState<BrandingProfileDto[] | null>(null);
  const [selectedBrandingId, setSelectedBrandingId] = useState<string>('');
  const [isBrandingEnabled, setIsBrandingEnabled] = useState(false);
  const mainBrandingProfileIdRef = useRef<string | null>(null);
  const [isRetentionEnabled, setIsRetentionEnabled] = useState(false);
  const retentionToggleId = useId();
  const retentionSelectId = useId();
  const retentionLabelId = useId();
  const brandingToggleId = useId();
  const brandingSelectId = useId();
  const brandingLabelId = useId();

  // File handling utilities
  const createFileKey = (file: File): string => 
    `${file.name}:${file.size}:${file.lastModified}`;

  const deduplicateFiles = (files: File[]): File[] => {
    const seen = new Set<string>();
    const deduped: File[] = [];
    
    for (const file of files) {
      const key = createFileKey(file);
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(file);
      }
    }
    
    return deduped;
  };

  // Dropzone handlers
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    
    setCompleted(false);
    clearErrors();

    // Clear any errors when new files are added
    if (error || uploadLimitError) {
      cancel();
    }
    
    setSelectedFiles((prev) => deduplicateFiles([...(prev ?? []), ...acceptedFiles]));
  }, [error, uploadLimitError, cancel, clearErrors]);

  // Image preview generation
  const [previews, setPreviews] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const map: Record<string, string> = {};
    
    if (selectedFiles) {
      for (const file of selectedFiles) {
        if (file.type.startsWith('image/')) {
          map[createFileKey(file)] = URL.createObjectURL(file);
        }
      }
    }
    
    setPreviews(map);
    
    return () => {
      Object.values(map).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  const hasFiles = Boolean(selectedFiles && selectedFiles.length > 0);
  const hasShareLink = Boolean(lastShareLink);
  const hasError = Boolean(error || uploadLimitError);

  const effectiveTier: UserTier = useMemo(() => {
    if (!isAuthenticated) {
      return UserTier.Anonymous;
    }

    if (hasLicense === true) {
      return UserTier.LicensedAccount;
    }

    // While license status is unknown (null) or explicitly false, apply free-tier rules.
    return UserTier.FreeAccount;
  }, [isAuthenticated, hasLicense]);

  const downloadLimitRange = useMemo(
    () => {
      if (effectiveTier === UserTier.Anonymous) {
        return { min: 1, max: 10, defaultValue: 10, enforced: true } as const;
      }

      if (effectiveTier === UserTier.FreeAccount) {
        return { min: 1, max: 20, defaultValue: 20, enforced: true } as const;
      }

      return { min: 1, max: 100000, defaultValue: 1, enforced: false } as const;
    },
    [effectiveTier]
  );

  const isMaxDownloadsEnforced = downloadLimitRange.enforced;

  // Retention period enforcement for anonymous users
  const retentionLimitRange = useMemo(
    () => {
      if (effectiveTier === UserTier.Anonymous) {
        return { enforced: true } as const;
      }

      return { enforced: false } as const;
    },
    [effectiveTier]
  );

  const isRetentionEnforced = retentionLimitRange.enforced;

  useEffect(() => {
    if (!hasFiles) {
      return;
    }

    if (downloadLimitRange.enforced) {
      setLimitDownloads(true);
      setMaxDownloadsInput(String(downloadLimitRange.defaultValue));
    }

    // Automatically enable retention for anonymous users
    if (retentionLimitRange.enforced) {
      setIsRetentionEnabled(true);
    }
  }, [downloadLimitRange, retentionLimitRange, hasFiles]);

  useEffect(() => {
    if (hasShareLink) {
      setShareLinkAcknowledged(false);
    }
  }, [hasShareLink]);

  useEffect(() => {
    if (!hasShareLink || shareLinkAcknowledged) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Modern browsers ignore custom strings here but require returnValue to be set.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasShareLink, shareLinkAcknowledged]);
  const showRetentionPeriod =
    hasFiles &&
    !hasShareLink &&
    !hasError &&
    (isAuthenticated || anonTosAccepted);

  const retentionControlsVisible = showRetentionPeriod && isRetentionEnabled;
  const showRetentionSelectInput = retentionControlsVisible;
  const effectiveRetentionPeriodValue = retentionControlsVisible ? retentionPeriodSliderValue : defaultRetentionPeriodIndex;
  // Build combined region list for the dropdown
  // Cloudflare R2 regions first (alphabetically), then Azure regions (alphabetically)
  const allStorageRegions = useMemo((): StorageRegion[] => {
    const availableCloudflare = new Set(cloudflareRegions);
    const cfRegions = getCloudflareStorageRegions()
      .filter((region) => availableCloudflare.has(region.code as CloudflareRegionHint))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Exclude EUAP and duplicate city regions from the list
    // EUAP regions are used by Microsoft for early testing and canary deployments
    // They are not recommended for production workloads due to less stability
    // Duplicate city regions are excluded to show only one region per city
    // May be re-enabled in the future if there's a use case for them
    const EXCLUDED_REGIONS = [
      'cueu', // US - Iowa (EUAP, duplicate city)
      'e2eu', // US - Virginia (EUAP, duplicate city)
      'eus2', // US - Virginia (duplicate city)
      'auc2', // Australia - Canberra (duplicate city)
    ];
    
    const azureRegionObjects = getAzureStorageRegions(azureRegions)
      .filter((region) => !EXCLUDED_REGIONS.includes(region.code))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    const seen = new Set<string>();
    const combined: StorageRegion[] = [];

    // Add Cloudflare regions first
    for (const region of cfRegions) {
      const key = `${region.provider}:${region.code}`;
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(region);
      }
    }

    // Add Azure regions after
    for (const region of azureRegionObjects) {
      const key = `${region.provider}:${region.code}`;
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(region);
      }
    }

    return combined;
  }, [cloudflareRegions, azureRegions]);

  const brandingOptions = useMemo(() => {
    if (!brandingProfiles || brandingProfiles.length === 0) {
      return [];
    }
    const baseOptions = brandingProfiles.map((profile) => ({
      id: String(profile.id),
      name: profile.name,
    }));
    return [
      ...baseOptions,
      { id: 'default', name: t('upload.brandingDefault', 'Default') },
    ];
  }, [brandingProfiles, t]);

  const showBrandingFeature = isAuthenticated && brandingProfiles !== null && brandingProfiles.length > 0;
  const showBrandingSelectInput = showBrandingFeature && isBrandingEnabled;

  useEffect(() => {
    if (!isAuthenticated) {
      setHasLicense(null);
      setBrandingProfiles(null);
      setSelectedBrandingId('');
      setIsBrandingEnabled(false);
      mainBrandingProfileIdRef.current = null;
      return;
    }

    // Skip branding profiles API call on region subdomains (e.g., brso.sendallfiles.com, weur.sendallfiles.com)
    // Region subdomains are used for storage routing and don't support branding features
    if (isRegionSubdomain()) {
      setBrandingProfiles([]);
      setSelectedBrandingId('');
      setIsBrandingEnabled(false);
      mainBrandingProfileIdRef.current = null;
      return;
    }

    // Simple loading for authenticated users
    LicenseService.getJoinedBrandingProfiles()
      .then((profiles) => {
        setBrandingProfiles(profiles);
        const mainProfile = profiles.find((profile) => profile.isMainProfile) ?? null;
        mainBrandingProfileIdRef.current = mainProfile ? String(mainProfile.id) : null;

        // Only set initial selection if user hasn't made a choice yet
        setSelectedBrandingId((current) => {
          if (current === '') {
            const initialId = mainProfile ? String(mainProfile.id) : profiles[0] ? String(profiles[0].id) : '';
            return initialId;
          }
          return current;
        });
      })
      .catch(() => {
        setBrandingProfiles([]);
      });
  }, [isAuthenticated]);


  const handleRetentionToggleChange = useCallback(
    (enabled: boolean) => {
      // Check if user is trying to disable retention for anonymous users
      if (!enabled && isRetentionEnforced) {
        if (!isAuthenticated) {
          toast.warning(
            t(
              'upload.retentionPeriod.anonUpsell',
              'Log in or create an account to disable retention period.'
            )
          );
        }
        return; // Don't allow disabling
      }

      setIsRetentionEnabled(enabled);
      if (!enabled) {
        setRetentionPeriodSliderValue(defaultRetentionPeriodIndex);
      }
    },
    [defaultRetentionPeriodIndex, isRetentionEnforced, isAuthenticated, t]
  );

  const handleBrandingToggleChange = useCallback(
    (enabled: boolean) => {
      setIsBrandingEnabled(enabled);
    },
    []
  );

  // Determine which branding profile to use based on the rules
  const getEffectiveBrandingProfile = useCallback((): BrandingProfileDto | null => {
    if (!brandingProfiles || brandingProfiles.length === 0) {
      return null;
    }

    if (isBrandingEnabled) {
      // If toggled on: use selected profile or Default (null)
      if (selectedBrandingId === 'default') {
        return null; // Default selected = no branding
      }
      const selectedProfile = brandingProfiles.find(profile => String(profile.id) === selectedBrandingId) ?? null;
      return selectedProfile;
    } else {
      // If toggled off: use main profile or none
      const mainProfile = brandingProfiles.find(profile => profile.isMainProfile) ?? null;
      return mainProfile;
    }
  }, [isBrandingEnabled, selectedBrandingId, brandingProfiles]);

  const loadRegions = useCallback(async () => {
    if (regionsLoaded) {
      return;
    }

    try {
      const [available, regionData] = await Promise.all([
        getRegions(),
        getCurrentRegion()
      ]);

      const uniqueAzure = Array.from(new Set(available ?? []));
      setAzureRegions(uniqueAzure);

      // Set all 4 Cloudflare regions as available
      setCloudflareRegions([...CLOUDFLARE_REGIONS_ORDER]);

      const normalizedCurrent = regionData?.currentRegion?.toLowerCase() ?? undefined;
      const availableAzureLower = uniqueAzure.map((code) => code.toLowerCase());
      const validCurrentAzure =
        normalizedCurrent && availableAzureLower.includes(normalizedCurrent)
          ? normalizedCurrent
          : undefined;
      setCurrentAzureRegion(validCurrentAzure || undefined);

      const azureRegionObjects = getAzureStorageRegions(uniqueAzure);

      // Default region selection priority:
      // 1. User's country-based region (if available)
      // 2. Current Azure region from backend
      // 3. Cloudflare region mapped from current Azure region
      // 4. First Cloudflare region as fallback
      let initialRegion: StorageRegion | undefined;

      // Priority 1: Try to match user's country to an Azure region
      if (regionData?.userCountry) {
        const countryCode = regionData.userCountry.toUpperCase();
        const countryRegionCode = COUNTRY_TO_AZURE_REGION[countryCode];
        
        if (countryRegionCode && availableAzureLower.includes(countryRegionCode.toLowerCase())) {
          const countryMatch = azureRegionObjects.find(
            (region) => region.code === countryRegionCode.toLowerCase()
          );
          if (countryMatch) {
            initialRegion = countryMatch;
          }
        }
      }

      // Priority 2: Fall back to current Azure region if no country match
      if (!initialRegion && validCurrentAzure) {
        const azureMatch = azureRegionObjects.find(
          (region) => region.code === validCurrentAzure.toLowerCase()
        );
        if (azureMatch) {
          initialRegion = azureMatch;
        }
      }

      if (
        !initialRegion &&
        normalizedCurrent &&
        isCloudflareRegion(normalizedCurrent as CloudflareRegionHint)
      ) {
        const cfCode = normalizedCurrent as CloudflareRegionHint;
        initialRegion = {
          code: cfCode,
          provider: 'cloudflare',
          displayName: CLOUDFLARE_REGION_DISPLAY_NAMES[cfCode] || cfCode.toUpperCase(),
        };
      }

      if (!initialRegion && validCurrentAzure) {
        const cfForCurrent = getCloudflareRegionForAzure(validCurrentAzure);
        if (cfForCurrent) {
          initialRegion = {
            code: cfForCurrent,
            provider: 'cloudflare',
            displayName: CLOUDFLARE_REGION_DISPLAY_NAMES[cfForCurrent] || cfForCurrent.toUpperCase(),
          };
        }
      }

      if (!initialRegion) {
        // Default to first Cloudflare region
        const firstCf = CLOUDFLARE_REGIONS_ORDER[0];
        initialRegion = {
          code: firstCf,
          provider: 'cloudflare',
          displayName: CLOUDFLARE_REGION_DISPLAY_NAMES[firstCf] || firstCf.toUpperCase(),
        };
      }

      setSelectedRegion(initialRegion);
    } catch {
      setAzureRegions([]);
      setCloudflareRegions([]);
      setCurrentAzureRegion(undefined);
      setSelectedRegion(undefined);
    } finally {
      setRegionsLoaded(true);
    }
  }, [regionsLoaded]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isUploading || (completed && Boolean(lastShareLink) && !error && !uploadLimitError),
    noClick: false,
    noKeyboard: true
  });

  useEffect(() => {
    const handleWindowDragOver = (event: DragEvent) => {
      if (!event.dataTransfer) {
        return;
      }
      const hasFilesType = Array.from(event.dataTransfer.types).includes('Files');
      if (!hasFilesType) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.uploader-panel')) {
        return;
      }
      event.preventDefault();
    };

    const handleWindowDrop = (event: DragEvent) => {
      if (!event.dataTransfer) {
        return;
      }
      const files = Array.from(event.dataTransfer.files || []);
      if (files.length === 0) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && target.closest('.uploader-panel')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (isUploading || (completed && Boolean(lastShareLink) && !error && !uploadLimitError)) {
        return;
      }
      onDrop(files);
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [onDrop, isUploading, completed, lastShareLink, error, uploadLimitError]);

  // Progress calculations
  const transferredBytes = useMemo(
    () => progress.reduce((sum, p) => sum + p.transferredBytes, 0),
    [progress]
  );

  const progressByKey = useMemo(() => {
    const map = new Map<string, { transferred: number; total?: number; status: string }>();
    
    for (const p of progress) {
      const descriptor = resolveDescriptor(p.fileId);
      if (!descriptor) continue;
      
      // Create a proper key from descriptor properties
      const key = `${descriptor.name}:${descriptor.size}:${descriptor.lastModified}`;
      map.set(key, { 
        transferred: p.transferredBytes, 
        total: p.totalBytes, 
        status: p.status 
      });
    }
    
    return map;
  }, [progress, resolveDescriptor]);

  const totalSelectedBytes = useMemo(
    () => (selectedFiles ?? []).reduce((sum, file) => sum + file.size, 0),
    [selectedFiles]
  );

  const showProgress = progress.length > 0 || isUploading;

  const handleRegionToggleChange = useCallback((enabled: boolean) => {
    setIsRegionEnabled(enabled);

    if (enabled) {
      void loadRegions();
    }
  }, [loadRegions]);

  // File management handlers
  const handleRemoveFile = useCallback((fileToRemove: File) => {
    clearErrors();
    setSelectedFiles((prev) => {
      if (!prev) return prev;
      
      const next = prev.filter((file) => file !== fileToRemove);
      if (next.length === 0) {
        setCompleted(false);
      }
      return next;
    });
  }, [clearErrors]);

  const handleUpload = useCallback(async () => {
    if (!selectedFiles) return;

    if (selectedFiles.some((file) => file.name.toLowerCase().endsWith('.torrent'))) {
      toast.error(t('common.forbiddenExtensions', 'Some files have forbidden extensions!'));
      return;
    }

    const { unit, amount } = sliderIndexToRetentionPeriod(effectiveRetentionPeriodValue);
    const retentionPeriodCode = buildRetentionPeriodCode(unit, amount);
    const expiresAtIso = computeExpiresAtIso(unit, amount);

    const trimmedPassword = password.trim();
    const hasPassword = isPasswordProtected && trimmedPassword.length > 0;

    let maxDownloads: number | undefined;
    const trimmedMax = maxDownloadsInput.trim();
    const shouldApplyDownloadLimit = limitDownloads || downloadLimitRange.enforced;
    if (shouldApplyDownloadLimit && trimmedMax.length > 0) {
      const parsed = Number.parseInt(trimmedMax, 10);
      if (!Number.isNaN(parsed)) {
        const clamped = Math.min(downloadLimitRange.max, Math.max(downloadLimitRange.min, parsed));
        maxDownloads = clamped;
      }
    }

    if (downloadLimitRange.enforced && (typeof maxDownloads !== 'number' || maxDownloads <= 0)) {
      maxDownloads = downloadLimitRange.defaultValue;
    }

    const options: {
      hasPassword?: boolean;
      password?: string;
      maxDownloads?: number;
      notifyByEmail?: boolean;
      regionKey?: string;
      brandingProfileId?: string;
    } = {};

    if (hasPassword) {
      options.hasPassword = true;
      options.password = trimmedPassword;
    }

    setPassword('');
    setIsPasswordProtected(false);

    if (typeof maxDownloads === 'number') {
      options.maxDownloads = maxDownloads;
    }

    if (isAuthenticated && notifyByEmail) {
      options.notifyByEmail = true;
    }

    if (isRegionEnabled && selectedRegion) {
      // Use the selected region code directly (works for both Cloudflare and Azure)
      options.regionKey = selectedRegion.code;
    }

    // Use the effective branding profile based on our rules
    const effectiveBrandingProfile = getEffectiveBrandingProfile();

    if (effectiveBrandingProfile) {
      options.brandingProfileId = String(effectiveBrandingProfile.id);
    }

    try {
      const shareLink = await createUpload(selectedFiles, retentionPeriodCode, expiresAtIso, options);
      setCompleted(Boolean(shareLink));
    } catch {
      // Errors handled by hook state
      setCompleted(false);
    }
  }, [
    selectedFiles,
    createUpload,
    retentionPeriodSliderValue,
    isPasswordProtected,
    password,
    maxDownloadsInput,
    limitDownloads,
    notifyByEmail,
    isAuthenticated,
    downloadLimitRange,
    isRegionEnabled,
    selectedRegion,
    azureRegions,
    cloudflareRegions,
    showBrandingSelectInput,
    selectedBrandingId,
    effectiveRetentionPeriodValue,
  ]);

  const handleCancel = useCallback(() => {
    cancel();
    setSelectedFiles(null);
    setCompleted(false);
  }, [cancel]);

  const handleSendMore = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  // Component state calculations
  const canRemoveFiles = !isUploading && (!completed || Boolean(error) || Boolean(uploadLimitError));

  // The upgrade paywall: opens once per size-limit rejection for anonymous and free users (licensed
  // users manage capacity through seat adjustment instead - the inline alert covers them).
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  useEffect(() => {
    const failureReason = uploadLimitError?.details?.failureReason ?? '';
    if (uploadLimitError && failureReason === 'SizeLimitExceeded' && hasLicense !== true) {
      setShowUpgradeModal(true);
    }
  }, [uploadLimitError, hasLicense]);

  const limitAlertConfig = useMemo(() => {
    if (!uploadLimitError) {
      return null;
    }

    const details = uploadLimitError.details ?? {};
    const failureReason = details.failureReason ?? 'SizeLimitExceeded';

    const isAuthenticatedUser = isAuthenticated;
    const hasActiveLicense = hasLicense === true;

    let message: ReactNode = uploadLimitError.message;
    if (
      failureReason === 'SizeLimitExceeded' &&
      typeof details.currentUsageBytes === 'number' &&
      typeof details.limitBytes === 'number'
    ) {
      const usageText = t('limits.uploadLimitExceeded', {
        used: formatBytes(details.currentUsageBytes),
        limit: formatBytes(details.limitBytes),
      });

      const hintText = t('limits.uploadLimitExceededHint', 'to send more.');

      if (!isAuthenticatedUser) {
        message = (
          <>
            {t('limits.uploadLimitExceededTitle', 'Upload limit exceeded!')}{' '}
            <Link to="/login" className="uploader-inline-link">{t('limits.loginCta')}</Link>{' '}
            {t('common.or')}{' '}
            <Link to="/signup" className="uploader-inline-link">{t('limits.signupCta')}</Link>{' '}
            {hintText}{' '}
            {usageText}
          </>
        );
      } else if (hasActiveLicense) {
        message = (
          <>
            {t('limits.uploadLimitExceededTitle', 'Upload limit exceeded!')}{' '}
            <Link
              to="/dashboard/licensing/manage"
              hash="seatAdjustment"
              className="uploader-subscribe-link"
            >
              {t('limits.increaseLicenseCapacityCta', 'Increase your license capacity')}
            </Link>{' '}
            {hintText}{' '}
            {usageText}
          </>
        );
      } else {
        message = (
          <>
            {t('limits.uploadLimitExceededTitle', 'Upload limit exceeded!')}{' '}
            <Link to="/dashboard" className="uploader-subscribe-link">{t('limits.buyLicenseCta', 'Subscribe')}</Link>{' '}
            {hintText}{' '}
            {usageText}
          </>
        );
      }
    }

    const actions: UploadLimitAlertAction[] = [];

    // No CTA buttons for either anonymous or authenticated users; all navigation is inline in the message.

    return { message, actions };
  }, [uploadLimitError, t, navigate, isAuthenticated, hasLicense]);

  // Notify parent when presence of files changes
  useEffect(() => {
    if (onHasFilesChange) {
      onHasFilesChange(hasFiles);
    }
  }, [hasFiles, onHasFilesChange]);

  useEffect(() => () => {
    if (acceptDelayRef.current) {
      window.clearTimeout(acceptDelayRef.current);
    }
  }, []);

  const selectedRetentionPeriodInfo = useMemo(() => {
    const { unit, amount } = sliderIndexToRetentionPeriod(effectiveRetentionPeriodValue);
    let days = 0;
    if (unit === 'd') {
      days = amount;
    } else if (unit === 'm') {
      days = amount * 30;
    } else {
      days = amount * 365;
    }
    return { unit, amount, days };
  }, [effectiveRetentionPeriodValue]);

  const maxRetentionPeriodDays = useMemo(() => {
    if (!isRetentionEnabled) {
      return Number.POSITIVE_INFINITY;
    }
    if (effectiveTier === UserTier.Anonymous) {
      return 4;
    }
    if (effectiveTier === UserTier.FreeAccount) {
      return 15;
    }
    return 3650;
  }, [effectiveTier, isRetentionEnabled]);

  const isRetentionPeriodBeyondLimit = isRetentionEnabled && selectedRetentionPeriodInfo.days > maxRetentionPeriodDays;

  const retentionLimitMessage = useMemo(() => {
    if (!isRetentionPeriodBeyondLimit) {
      return null;
    }

    if (!isAuthenticated) {
      const prefix = t('upload.retentionPeriodLimit.anonymous', { maxDays: 4 });
      const suffix = t('upload.retentionPeriodLimit.anonymousSuffix');
      return (
        <>
          {prefix}{' '}
          <Link to="/login">
            {t('limits.loginCta')}
          </Link>{' '}
          {t('common.or')}{' '}
          <Link to="/signup">
            {t('upload.retentionPeriodLimit.signupCta')}
          </Link>{' '}
          {suffix}
        </>
      );
    }

    if (effectiveTier === UserTier.FreeAccount) {
      return t(
        'upload.retentionPeriodLimit.freeAccount',
        {
          maxDays: 15
        }
      );
    }

    return null;
  }, [isRetentionPeriodBeyondLimit, isAuthenticated, effectiveTier, t]);

  const hasAcceptedTermsOrAuthenticated = isAuthenticated || anonTosAccepted;
  const hasValidPasswordForUpload = !isPasswordProtected || password.trim().length > 0;
  const hasValidDownloadLimitForUpload = useMemo(() => {
    if (!limitDownloads && !downloadLimitRange.enforced) {
      return true;
    }

    const trimmed = maxDownloadsInput.trim();
    if (trimmed.length === 0) {
      return false;
    }

    const parsed = Number.parseInt(trimmed, 10);
    if (Number.isNaN(parsed)) {
      return false;
    }

    return parsed >= downloadLimitRange.min && parsed <= downloadLimitRange.max;
  }, [limitDownloads, maxDownloadsInput, downloadLimitRange]);

  const canUpload =
    hasAcceptedTermsOrAuthenticated &&
    !isRetentionPeriodBeyondLimit &&
    hasValidPasswordForUpload &&
    hasValidDownloadLimitForUpload;
  const shouldShowAnonTermsInline = !isAuthenticated && !anonTosAccepted && hasFiles && !hasShareLink && !hasError && !isUploading;

  const shouldShowMaxDownloadsInput = limitDownloads || isMaxDownloadsEnforced;

  // CSS classes
  const zoneClasses = [
    'uploader-zone',
    isDragActive ? 'drag-active' : '',
    (isUploading || (completed && hasShareLink)) ? 'disabled' : '',
    hasFiles ? 'has-files' : ''
  ].filter(Boolean).join(' ');

  const actionClasses = [
    'uploader-actions',
    hasShareLink && !isUploading ? 'uploader-actions--share-link' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      className="panel uploader-panel"
      onClick={(e) => {
        const target = e.target as HTMLElement | null;
        if (target && target.closest('.uploader-advanced')) {
          e.stopPropagation();
        }
      }}
    >
      <div {...getRootProps({ className: zoneClasses })}>
        <input
          {...getInputProps({
            'aria-label': t('upload.fileInputLabel', 'Add files to upload'),
          })}
        />
        
        {/* Initial empty state */}
        {!hasFiles && !completed && !isUploading && (
          <EmptyState
            title={t('upload.sendTitle')}
            description={dropzoneHintText}
            icon={<EmptyStateIcons.upload />}
            size="lg"
          />
        )}

        {/* Upload completed message */}
        {completed && !hasError && (
          <h2>{t('upload.uploadCompleted')}</h2>
        )}

        {/* Upload progress */}
        {!completed && hasFiles && (
          <UploadProgress
            transferredBytes={transferredBytes}
            totalBytes={totalSelectedBytes}
            showProgress={showProgress}
          />
        )}

        {/* File list */}
        {hasFiles && selectedFiles && (
          <>
            {!isUploading && !completed && (
              <>
                <h2>{t('upload.readyToUpload')}</h2>
                <p>
                  {t('upload.fileCount', { count: selectedFiles.length })} ·{' '}
                  {t('upload.totalSize', { size: formatBytesToMB(totalSelectedBytes, 2, t) })}
                </p>
              </>
            )}
            <div className="file-chip-grid">
              {selectedFiles.map((file) => {
                const key = createFileKey(file);
                const thumbnail = previews[key];
                const fileProgress = progressByKey.get(key);
                
                return (
                  <FileChip
                    key={key}
                    file={file}
                    thumbnail={thumbnail}
                    canRemove={canRemoveFiles}
                    onRemove={handleRemoveFile}
                    progress={fileProgress}
                  />
                );
              })}
              {!isUploading && !completed && (
                <div className="file-chip file-chip--add-more">
                  <span className="file-chip-add-icon">+</span>
                  <span className="file-chip-add-text">{dropzoneHintText}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showRetentionPeriod && (
        <div className="uploader-advanced">
          <div className="uploader-retentionperiod">
            <label
              id={retentionLabelId}
              className="uploader-retentionperiod-toggle"
              htmlFor={retentionToggleId}
            >
              <span className="uploader-retentionperiod-label">{t('upload.retentionPeriodLabel')}</span>
              <span className="uploader-retentionperiod-toggle-switch">
                <input
                  id={retentionToggleId}
                  type="checkbox"
                  className="uploader-retentionperiod-toggle-input"
                  checked={isRetentionEnforced ? true : isRetentionEnabled}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;

                    if (isRetentionEnforced) {
                      if (!nextChecked) {
                        if (!isAuthenticated) {
                          toast.warning(
                            t(
                              'upload.retentionPeriod.anonUpsell',
                              'Log in or create an account to disable retention period.'
                            )
                          );
                        }
                      }
                      return; // Don't allow changing state when enforced
                    }

                    handleRetentionToggleChange(nextChecked);
                  }}
                  disabled={isUploading}
                />
                <span className="uploader-retentionperiod-toggle-slider" aria-hidden="true" />
              </span>
            </label>
            {showRetentionSelectInput && (
              <div className="uploader-retentionperiod-select-wrapper">
                <select
                  id={retentionSelectId}
                  value={retentionPeriodSliderValue}
                  onChange={(e) => setRetentionPeriodSliderValue(Number(e.target.value))}
                  className="uploader-retentionperiod-select"
                  disabled={isUploading}
                  aria-labelledby={retentionLabelId}
                >
                  {Array.from({ length: RETENTION_PERIOD_COMBOBOX_MAX_INDEX }, (_, i) => i + 1).map((index) => {
                    const { unit, amount } = sliderIndexToRetentionPeriod(index);
                    let label: string;
                    if (unit === 'd') {
                      label = t('upload.retentionPeriod.days', { count: amount });
                    } else if (unit === 'm') {
                      label = t('upload.retentionPeriod.months', { count: amount });
                    } else {
                      label = t('upload.retentionPeriod.years', { count: amount });
                    }
                    return (
                      <option key={index} value={index}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
          {showRetentionSelectInput && retentionLimitMessage && (
            <div className="uploader-retentionperiod-warning">{retentionLimitMessage}</div>
          )}
          <div className="uploader-region">
            <label className="uploader-region-toggle">
              <span className="uploader-region-label">
                {t('upload.regionLabel', 'Data hosting region')}
              </span>
              <span className="uploader-region-toggle-switch">
                <input
                  type="checkbox"
                  className="uploader-region-toggle-input"
                  checked={isRegionEnabled}
                  onChange={(e) => handleRegionToggleChange(e.target.checked)}
                  disabled={isUploading}
                />
                <span className="uploader-region-toggle-slider" aria-hidden="true" />
              </span>
            </label>
            {isRegionEnabled && allStorageRegions.length > 0 && (
              <div className="uploader-region-select-wrapper">
                <select
                  id="region-select"
                  className="uploader-region-select"
                  value={selectedRegion ? `${selectedRegion.provider}:${selectedRegion.code}` : ''}
                  onChange={(e) => {
                    const [provider, code] = e.target.value.split(':') as [StorageProvider, string];
                    const region = allStorageRegions.find((r) => r.provider === provider && r.code === code);
                    setSelectedRegion(region);
                  }}
                  disabled={isUploading}
                >
                  {(() => {
                    const cfRegions = allStorageRegions.filter(r => r.provider === 'cloudflare');
                    const azureRegions = allStorageRegions.filter(r => r.provider === 'azure');
                    
                    return (
                      <>
                        {cfRegions.length > 0 && (
                          <optgroup label={t('common.regions', 'Regions')}>
                            {cfRegions.map((region) => {
                              const label = t(`upload.region.${region.code}`, {
                                defaultValue: region.displayName ?? region.code.toUpperCase(),
                              });
                              return (
                                <option key={`${region.provider}:${region.code}`} value={`${region.provider}:${region.code}`}>
                                  {label}
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                        {azureRegions.length > 0 && (
                          <optgroup label={t('common.zones', 'Zones')}>
                            {azureRegions.map((region) => {
                              const label = t(`upload.region.${region.code}`, {
                                defaultValue: region.displayName ?? region.code.toUpperCase(),
                              });
                              return (
                                <option key={`${region.provider}:${region.code}`} value={`${region.provider}:${region.code}`}>
                                  {label}
                                </option>
                              );
                            })}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>
            )}
          </div>
          <div className="uploader-password">
            <label className="uploader-password-toggle">
              <span className="uploader-password-label">
                {t('upload.passwordProtectedLabel', 'Password protection')}
              </span>
              <span className="uploader-password-toggle-switch">
                <input
                  type="checkbox"
                  className="uploader-password-toggle-input"
                  checked={isPasswordProtected}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setIsPasswordProtected(enabled);
                    setPassword('');
                  }}
                  disabled={isUploading}
                />
                <span className="uploader-password-toggle-slider" aria-hidden="true" />
              </span>
            </label>
            {isPasswordProtected && (
              <div className="uploader-password-input">
                <SecretInputBox
                  value={password}
                  masked
                  allowToggleMask
                  ariaLabel={t('upload.passwordPlaceholder', 'Enter password')}
                  onChange={(e) => setPassword(e.target.value)}
                  readOnly={isUploading}
                />
              </div>
            )}
          </div>
          <div className="uploader-max-downloads">
            <label className="uploader-max-downloads-toggle">
              <span className="uploader-max-downloads-label">
                {t('upload.maxDownloadsLabel', 'Limit downloads')}
              </span>
              <span className="uploader-max-downloads-toggle-switch">
                <input
                  type="checkbox"
                  className="uploader-max-downloads-toggle-input"
                  checked={isMaxDownloadsEnforced ? true : limitDownloads}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;

                    if (isMaxDownloadsEnforced) {
                      if (!nextChecked) {
                        if (!isAuthenticated) {
                          toast.warning(
                            t(
                              'upload.maxDownloads.anonUpsell',
                              'Log in or create an account to use a higher download limit.'
                            )
                          );
                        } else if (effectiveTier === UserTier.FreeAccount) {
                          toast.warning(
                            t(
                              'upload.maxDownloads.freeUpsell',
                              'Subscribe to a license to unlock unlimited downloads.'
                            )
                          );
                        }
                      }
                      return;
                    }

                    const enabled = nextChecked;
                    setLimitDownloads(enabled);
                    if (enabled && maxDownloadsInput.trim().length === 0) {
                      setMaxDownloadsInput(String(downloadLimitRange.defaultValue));
                    }
                  }}
                  disabled={isUploading}
                />
                <span className="uploader-max-downloads-toggle-slider" aria-hidden="true" />
              </span>
            </label>
            {shouldShowMaxDownloadsInput && (
              <div className="uploader-max-downloads-input">
                <input
                  type="number"
                  min={downloadLimitRange.min}
                  max={downloadLimitRange.max}
                  value={maxDownloadsInput}
                  onChange={(e) => setMaxDownloadsInput(e.target.value)}
                  disabled={isUploading}
                />
              </div>
            )}
          </div>
          <div className="uploader-notify-email">
            <label
              className={`uploader-notify-email-toggle${!isAuthenticated ? ' uploader-notify-email-toggle--disabled' : ''}`}
              onClick={() => {
                if (!isAuthenticated) {
                  toast.warning(
                    t(
                      'upload.notifyByEmail.anonUpsell',
                      'Log in or create a free account to use the Email notification feature!'
                    )
                  );
                }
              }}
            >
              <span className="uploader-notify-email-label">
                {t('upload.notifyByEmailLabel', 'Notify me by email')}
              </span>
              <span className="uploader-notify-email-toggle-switch">
                <input
                  type="checkbox"
                  className="uploader-notify-email-toggle-input"
                  checked={isAuthenticated ? notifyByEmail : false}
                  onChange={(e) => {
                    setNotifyByEmail(e.target.checked);
                  }}
                  disabled={isUploading || !isAuthenticated}
                />
                <span className="uploader-notify-email-toggle-slider" aria-hidden="true" />
              </span>
            </label>
          </div>
          {showBrandingFeature && (
            <div className="uploader-branding">
              <label
                id={brandingLabelId}
                className="uploader-branding-toggle"
                htmlFor={brandingToggleId}
              >
                <span className="uploader-branding-label">
                  {t('upload.brandingLabel', 'Branding')}
                </span>
                <span className="uploader-branding-toggle-switch" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isUploading) {
                    handleBrandingToggleChange(!isBrandingEnabled);
                  }
                }}>
                  <input
                    id={brandingToggleId}
                    type="checkbox"
                    className="uploader-branding-toggle-input"
                    checked={isBrandingEnabled}
                    onChange={(e) => handleBrandingToggleChange(e.target.checked)}
                    disabled={isUploading}
                  />
                  <span className="uploader-branding-toggle-slider" aria-hidden="true" />
                </span>
              </label>
              {showBrandingSelectInput && (
                <div className="uploader-branding-select-wrapper">
                  <select
                    id={brandingSelectId}
                    className="uploader-retentionperiod-select"
                    value={selectedBrandingId}
                    onChange={(e) => setSelectedBrandingId(e.target.value)}
                    disabled={isUploading}
                    aria-labelledby={brandingLabelId}
                  >
                    {selectedBrandingId === '' && (
                      <option value="">{t('upload.brandingPlaceholder', 'Select branding')}</option>
                    )}
                    {brandingOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error displays */}
      {error && <p role="alert">⚠️ {error}</p>}
      
      {uploadLimitError && (
        <UploadLimitAlert
          error={uploadLimitError}
          message={limitAlertConfig?.message}
          actions={limitAlertConfig?.actions}
        />
      )}

      {uploadLimitError && (
        <UpgradePromptModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          details={uploadLimitError.details ?? {}}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Action buttons */}
      <div className={actionClasses}>
        <UploadActions
          isUploading={isUploading}
          hasFiles={hasFiles}
          hasShareLink={hasShareLink}
          hasError={hasError}
          shareLink={lastShareLink || undefined}
          onUpload={handleUpload}
          onCancel={handleCancel}
          onSendMore={handleSendMore}
          canUpload={canUpload}
          canShowUpload={hasAcceptedTermsOrAuthenticated}
          shareLinkNeedsConfirm={hasShareLink && !shareLinkAcknowledged}
          onShareLinkAcknowledged={() => setShareLinkAcknowledged(true)}
        />
        {shouldShowAnonTermsInline && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
            }}
          >
            <UploadPrivacyConsentChoice />
            <TermsOfServiceAgreement
              checked={anonTosAccepted}
              onChange={(nextValue) => {
                if (acceptDelayRef.current) {
                  window.clearTimeout(acceptDelayRef.current);
                  acceptDelayRef.current = null;
                }
                if (nextValue) {
                  acceptDelayRef.current = window.setTimeout(() => {
                    setAnonTosAccepted(true);
                    acceptDelayRef.current = null;
                  }, 200);
                } else {
                  setAnonTosAccepted(false);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UploaderZone;
